import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { PropertyMappings } from "../PropertyMappingSelector";
import { GlobalPropertyMappingSelectorProps } from "./GlobalPropertyMappingSelector.types";
import { useGlobalPropertyMappingSelectorViewModel } from "./GlobalPropertyMappingSelector.vm";
import { DefaultImportTableColumns, ImportTableColumn } from "../../../../utilities/types";
import { columnDisplayNames } from "../../../admin/ImportSettings/ImportSettings.types";
import "./GlobalPropertyMappingSelector.css";

/** Fields from PropertyMappings that have a corresponding ImportTableColumn */
const PROPERTY_MAPPING_FIELDS: Set<keyof PropertyMappings> = new Set([
	"grower",
	"farm",
	"field",
	"fieldId",
	"labName",
	"labInstructions",
	"submissionNotes",
	"yieldGoal",
	"submitterName",
	"submitterEmail",
	"growerPhoneNumber",
	"eventId",
	"labSubmissionCode",
	"externalId",
	"externalFarmId",
	"externalLabId",
	"dateFieldReadyEstimated",
	"density",
	"depth",
	"frequencyOfMicros",
	"testPackage",
	"fieldPriority",
	"nextCrop",
	"lastCrop",
	"branch",
	"salesperson",
	"salespersonMobilePhone",
]);

/**
 * Generate field configs in the order defined by DefaultImportTableColumns.
 * This ensures property mapping dropdowns match the ImportTable column order.
 */
const FIELD_CONFIGS: Array<{ key: keyof PropertyMappings; label: string }> = (
	Object.keys(DefaultImportTableColumns) as ImportTableColumn[]
)
	.filter((key) => PROPERTY_MAPPING_FIELDS.has(key as keyof PropertyMappings))
	.map((key) => ({ key: key as keyof PropertyMappings, label: columnDisplayNames[key] }));

export default function GlobalPropertyMappingSelector(props: GlobalPropertyMappingSelectorProps) {
	const vm = useGlobalPropertyMappingSelectorViewModel(props);

	if (!vm.shouldRender) {
		return null;
	}

	// Filter fields based on company visibility settings
	const isFieldVisible = (field: keyof PropertyMappings) => {
		if (field === "uniqueKey") return true;
		const column = field as ImportTableColumn;
		const settings = props.company?.portalSettings?.importTableColumns?.[column];
		return settings?.visible ?? DefaultImportTableColumns[column]?.visible ?? true;
	};
	const visibleFields = FIELD_CONFIGS.filter((field) => isFieldVisible(field.key));

	const renderGlobalAutocomplete = (field: keyof PropertyMappings, label: string) => {
		const selectedProperties = vm.selectedValues[field] || [];

		// Generate helper text showing example values from selected properties
		const getHelperText = () => {
			if (selectedProperties.length === 0) return undefined;

			const exampleGroups = selectedProperties
				.map((prop) => prop.examples?.slice(0, 2).join(", ") || "")
				.filter((ex) => ex.length > 0);

			if (exampleGroups.length === 0) return undefined;

			return exampleGroups.join("\n---\n");
		};

		return (
			<Box>
				<Autocomplete
					multiple
					options={vm.availableProperties}
					getOptionLabel={(option) => option.name}
					value={selectedProperties}
					onChange={(_, value) => vm.handleSelectionChange(field, value)}
					renderOption={({ key, ...props }, option) => (
						<Box component="li" key={key} {...props}>
							<Box>
								<Typography variant="body2">{option.name}</Typography>
								{option.examples && option.examples.length > 0 && (
									<Box>
										{option.examples.slice(0, 3).map((example, index) => (
											<Typography key={index} variant="caption" color="text.secondary" className="option-example">
												{example}
											</Typography>
										))}
									</Box>
								)}
							</Box>
						</Box>
					)}
					renderTags={(tagValue, getTagProps) =>
						tagValue.map((option, index) => (
							<Chip label={option.name} {...getTagProps({ index })} size="small" key={option.name} />
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label={label}
							size="small"
							placeholder={selectedProperties.length === 0 ? "Select properties" : ""}
							helperText={getHelperText()}
							FormHelperTextProps={{ sx: { whiteSpace: "pre-line" } }}
						/>
					)}
					clearOnEscape={false}
					isOptionEqualToValue={(option, value) => option.name === value?.name}
				/>
			</Box>
		);
	};

	return (
		<Box className="global-property-mapping-selector">
			<Typography variant="h6" gutterBottom>
				Property Mapping
			</Typography>
			<Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
				Select properties from the uploaded files that correspond to each Rogo property. Missing values will need to be
				filled in manually in the table that follows.
			</Typography>
			{vm.showAutoGuessNotice && (
				<Typography variant="body2" className="auto-guess-notice">
					We&apos;ve automatically guessed some property mappings for you. Please review and adjust as needed.
				</Typography>
			)}
			<Grid container spacing={2}>
				{visibleFields.map((field) => (
					<Grid size={{ xs: 12, md: 6, lg: 3 }} key={field.key}>
						{renderGlobalAutocomplete(field.key, field.label)}
					</Grid>
				))}
			</Grid>
		</Box>
	);
}
